import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        
        if (code && state) {
          // Clear any existing auth data first
          sessionStorage.removeItem('auth_code');
          sessionStorage.removeItem('auth_state');
          sessionStorage.removeItem('auth_timestamp');
          
          // Store new auth data with timestamp for expiration
          sessionStorage.setItem('auth_code', code);
          sessionStorage.setItem('auth_state', state);
          sessionStorage.setItem('auth_timestamp', new Date().getTime().toString());
          
          // Set a timeout to clear the auth data after 5 minutes
          // This is a backup in case the main application fails to process the callback
          setTimeout(() => {
            sessionStorage.removeItem('auth_code');
            sessionStorage.removeItem('auth_state');
            sessionStorage.removeItem('auth_timestamp');
          }, 5 * 60 * 1000);
          
          // Clear URL parameters to prevent leaking the code in browser history
          if (window.history && window.history.replaceState) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          
          // Redirect back to the main application
          window.location.href = '/';
        } else {
          throw new Error('No code or state found in URL');
        }
      } catch (error) {
        console.error('Error during authentication callback:', error);
        setError(error instanceof Error ? error.message : 'An error occurred');
      }
    };
    
    handleCallback();
  }, [navigate, searchParams]);
  
  // Inline styles matching auth.html exactly
  const styles = {
    body: {
      fontFamily: 'Arial, sans-serif',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      margin: 0,
      backgroundColor: '#f5f5f5',
    },
    container: {
      textAlign: 'center' as const,
      padding: '2rem',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    },
    spinner: {
      border: '4px solid rgba(0, 0, 0, 0.1)',
      borderRadius: '50%',
      borderTop: '4px solid #3498db',
      width: '40px',
      height: '40px',
      margin: '20px auto',
      animation: 'spin 1s linear infinite',
    },
    errorText: {
      color: 'red',
    }
  };
  
  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={styles.body}>
        <div style={styles.container}>
          {error ? (
            <>
              <h2>Authentication Error</h2>
              <p>There was an error processing your authentication:</p>
              <p style={styles.errorText}>{error}</p>
              <p><a href="/">Return to Home</a></p>
            </>
          ) : (
            <>
              <h2>Processing Authentication</h2>
              <div style={styles.spinner}></div>
              <p>Please wait while we complete the authentication process...</p>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AuthCallback;